// Разбор вставленного из Excel куска на «фамилия, сумма за период, норма часов».
//
// ## Почему порядок колонок задан жёстко, а не угадывается
//
// Соблазн был: из двух чисел в строке то, что не больше 744, — норма, остальное сумма.
// Работает на живых данных и разваливается на «85» вместо «85 000»: заказчик пишет суммы и в
// тысячах тоже. Угадывание величины денег — ровно то, чего правила проекта не разрешают
// (`.claude/rules/money.md`), а цена ошибки здесь — тариф человека на месяц.
//
// Поэтому: первая колонка — фамилия, вторая — сумма за период, третья — норма часов. Ровно
// так, как написано над полем вставки. Строка, где это не сходится, не толкуется, а попадает
// в «не разобрали» — целиком, с исходным текстом, чтобы человек нашёл её у себя в книге.
//
// ## Числа не разбираются здесь
//
// Сумма уезжает строкой, как её вставили («85 000», «85000,00», «85 000 ₽»), и превращает её
// в копейки общий разбор денег кабинета. Второй разборщик рядом с ним однажды разойдётся с
// первым на пробеле-разделителе разрядов, и человек получит не ту зарплату.

/** Одна разобранная строка вставки. */
export interface СтрокаВставки {
  /** Как фамилия написана в книге: по ней ищем человека в списке. */
  фамилия: string;
  /** Сумма за период, как её вставили. Разбирается общим разбором денег. */
  сумма: string;
  /** Норма часов, как её вставили. */
  норма: string;
  /** Исходная строка целиком: попадает в отчёт, если разобрать не вышло. */
  исходная: string;
}

export function разобратьТарифы(текст: string): СтрокаВставки[] {
  return текст
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n')
    .map((строка) => строка.trim())
    .filter((строка) => строка !== '')
    .map(разобратьСтроку);
}

function разобратьСтроку(строка: string): СтрокаВставки {
  // Табуляция — то, чем Excel разделяет колонки в буфере. Точка с запятой и два пробела
  // подряд разрешены тоже: человек набирает такое руками, и отказывать ему незачем.
  const колонки = строка
    .split(/\t|;|\s{2,}/)
    .map((кусок) => кусок.trim())
    .filter((кусок) => кусок !== '');
  return {
    фамилия: колонки[0] ?? '',
    сумма: колонки[1] ?? '',
    норма: колонки[2] ?? '',
    исходная: строка,
  };
}

/**
 * Ключ поиска человека: фамилия и первая буква имени.
 *
 * Та же склейка, что в переносе книг заказчика (`tools/import/identity.py`): сравнивать только
 * фамилию нельзя — у него сорок сотрудников с однофамильцами, а сравнивать полностью нельзя
 * тоже: в книге «Абеленцев Н.», в программе «Абеленцев Николай».
 */
export function ключПоиска(имя: string): string {
  const части = имя
    .replace(/ё/gi, 'е')
    .toLowerCase()
    .split(/[\s.]+/)
    .filter((кусок) => кусок !== '');
  const фамилия = части[0] ?? '';
  const инициал = (части[1] ?? '').slice(0, 1);
  return инициал === '' ? фамилия : `${фамилия} ${инициал}`;
}

/** Только фамилия: запасной ключ, когда инициала нет ни в книге, ни в программе. */
export function ключФамилии(имя: string): string {
  return ключПоиска(имя).split(' ')[0] ?? '';
}
